import * as THREE from "three";
import type { Position } from "../types/grid.js";
import type { PropMetadata, ContainerMetadata, InteractMetadata } from "../types/tiled.js";
import { worldObjectUrl } from "../adventure/ActiveAdventure.js";
import { makeProceduralPropTexture, authoredImageToTexture } from "./propTextures.js";
import { INTERACTIVE_LAYER } from "./Raycaster.js";
import { EventBus } from "../EventBus.js";

/**
 * Free-standing prop rendering (W19).
 *
 * Small props (barrels, crates, chests, containers) render as a single
 * billboard quad. Large props (statues, obelisks, sarcophagi) render as
 * two perpendicular `PlaneGeometry` quads — the crossed-plane trick
 * reads as 3D from any yaw without a glTF pipeline.
 *
 * Authored art lives at
 * `public/<adventure>/world/<klass>/<slug>/<state>.png`.
 * `klass` is `"props"` (prop taxonomy key e.g. `barrel`) or `"containers"`
 * (closed/open-full/open-empty); the slug is the Tiled `prop_id` or
 * `container_id`. Missing PNGs fall back to a procedural canvas texture
 * so the map is still playable.
 *
 * No animation, no open/empty swap yet — the containers/keys/locks
 * initiative will add the state-swap wiring when the loot UI lands.
 */

const CELL_SIZE = 1;
const SMALL_SIZE: readonly [number, number] = [0.8, 1.0]; // w, h
const LARGE_SIZE: readonly [number, number] = [1.0, 1.6];
// Containers are furniture, not statues: chest-height, not wall-height.
// Width follows the authored art's 3:4 canvas. The quad bottom sinks
// slightly below floor level to swallow the art's built-in ~8% ground
// margin — otherwise the chest reads as hovering.
const CONTAINER_SIZE: readonly [number, number] = [0.5, 0.65];
const Y_OFFSET_SMALL = 0.5; // half-height so the sprite's feet sit on the floor
const Y_OFFSET_LARGE = 0.8;
const Y_OFFSET_CONTAINER = 0.3;

export type PropDescriptor =
  | { readonly kind: "prop"; readonly cell: Position; readonly meta: PropMetadata }
  | { readonly kind: "container"; readonly cell: Position; readonly meta: ContainerMetadata }
  | { readonly kind: "interact"; readonly cell: Position; readonly meta: InteractMetadata };

/** I15c — container visual state drives which PNG the record loads. */
type ContainerState = "closed" | "open-full" | "open-empty";
/** W21b — interact visual state. */
type InteractState = "idle" | "activated";

interface PropRecord {
  readonly group: THREE.Group;
  readonly materials: readonly THREE.Material[];
  readonly textures: readonly THREE.Texture[];
  /** I15c — set for container records so the event handlers can refetch art. */
  readonly container?: { readonly slug: string; state: ContainerState };
  /** W21b — set for interact records so activation can swap the PNG. */
  readonly interact?: { readonly slug: string; state: InteractState };
}

// ── Authored PNG loader ──────────────────────────────────────────────────────

type AuthoredImageState = HTMLImageElement | "failed";
const authoredImages = new Map<string, AuthoredImageState>();
const pendingLoads = new Map<string, Promise<HTMLImageElement | null>>();

let loaderOverride: ((url: string) => Promise<HTMLImageElement | null>) | null = null;

export function _setPropLoaderForTests(
  override: ((url: string) => Promise<HTMLImageElement | null>) | null,
): void {
  loaderOverride = override;
  authoredImages.clear();
  pendingLoads.clear();
}

function defaultLoader(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function loadAuthoredImage(cacheKey: string, url: string): Promise<HTMLImageElement | null> {
  const cached = authoredImages.get(cacheKey);
  if (cached === "failed") return Promise.resolve(null);
  if (cached) return Promise.resolve(cached);
  const inflight = pendingLoads.get(cacheKey);
  if (inflight) return inflight;

  const loader = loaderOverride ?? defaultLoader;
  const p = loader(url).then((img) => {
    pendingLoads.delete(cacheKey);
    if (img) {
      authoredImages.set(cacheKey, img);
      return img;
    }
    authoredImages.set(cacheKey, "failed");
    return null;
  });
  pendingLoads.set(cacheKey, p);
  return p;
}

// ── PropManager ──────────────────────────────────────────────────────────────

/** I15c — short label shown on the procedural fallback per container state. */
function labelForContainerState(state: ContainerState): string {
  if (state === "open-full") return "O";
  if (state === "open-empty") return "E";
  return "C";
}

/** W21b — neutral glyph for unauthored interact art. */
function labelForInteractSubtype(
  subtype: InteractMetadata["subtype"],
): string {
  if (subtype === "lever") return "L";
  if (subtype === "button") return "B";
  if (subtype === "plate") return "P";
  return "?";
}

export class PropManager {
  private readonly scene: THREE.Scene;
  private readonly records = new Map<string, PropRecord>();
  /** I15c — unsubscribe handles for EventBus wiring; released in `dispose`. */
  private readonly unsubscribes: Array<() => void> = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // I15c — swap container art on open/loot. `container_opened` moves to
    // the `open-full` PNG (lid up, contents visible); `container_looted`
    // follows with `open-empty` (same pose, drained). Records for props
    // (non-containers) are untouched because they have no `container` tag.
    this.unsubscribes.push(
      EventBus.on("world:container_opened", ({ containerId }) => {
        this.swapContainerState(containerId, "open-full");
      }),
      EventBus.on("world:container_looted", ({ containerId }) => {
        this.swapContainerState(containerId, "open-empty");
      }),
      // W21b — interact-object activation swaps idle → activated.
      EventBus.on("world:interact_activated", ({ interactId }) => {
        this.swapInteractState(interactId, "activated");
      }),
    );
  }

  spawn(descriptor: PropDescriptor): void {
    const [klass, slug, state, label] =
      descriptor.kind === "prop"
        ? (["props", descriptor.meta.prop_id, "idle", descriptor.meta.kind] as const)
        : descriptor.kind === "container"
          ? (["containers", descriptor.meta.container_id, "closed", "C"] as const)
          : ([
              "interacts",
              descriptor.meta.interact_id,
              "idle",
              labelForInteractSubtype(descriptor.meta.subtype),
            ] as const);

    const size = descriptor.kind === "prop" && descriptor.meta.size === "large" ? "large" : "small";

    const texture = makeProceduralPropTexture(slug, label);
    const isContainer = descriptor.kind === "container";
    const [w, h] = size === "large" ? LARGE_SIZE : isContainer ? CONTAINER_SIZE : SMALL_SIZE;
    const yCenter = size === "large" ? Y_OFFSET_LARGE : isContainer ? Y_OFFSET_CONTAINER : Y_OFFSET_SMALL;

    const group = new THREE.Group();
    group.position.set(descriptor.cell.x * CELL_SIZE, yCenter, descriptor.cell.z * CELL_SIZE);

    const materials: THREE.Material[] = [];
    const textures: THREE.Texture[] = [texture];

    if (size === "large") {
      // Crossed planes: two quads rotated 90° apart so the prop reads as 3D.
      for (const rot of [0, Math.PI / 2]) {
        const mat = new THREE.MeshStandardMaterial({
          map: texture,
          transparent: true,
          side: THREE.DoubleSide,
          roughness: 0.9,
          metalness: 0.0,
        });
        materials.push(mat);
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
        mesh.rotation.y = rot;
        group.add(mesh);
      }
    } else {
      // Billboard small prop: a single Sprite so it always faces the camera.
      const mat = new THREE.SpriteMaterial({ map: texture, transparent: true });
      materials.push(mat);
      const sprite = new THREE.Sprite(mat);
      sprite.scale.set(w, h, 1);
      // I15a / W21b: containers + click-mode interacts are clickable. Put the
      // sprite on the raycaster's interactive layer and stamp userData so
      // the InteractionDispatcher can route the hit.
      if (descriptor.kind === "container") {
        sprite.layers.set(INTERACTIVE_LAYER);
        sprite.userData = {
          instanceId: descriptor.meta.container_id,
          kind: "container",
          cellX: descriptor.cell.x,
          cellZ: descriptor.cell.z,
        };
      } else if (descriptor.kind === "interact" && descriptor.meta.trigger_mode === "click") {
        sprite.layers.set(INTERACTIVE_LAYER);
        sprite.userData = {
          instanceId: descriptor.meta.interact_id,
          kind: "interact",
          cellX: descriptor.cell.x,
          cellZ: descriptor.cell.z,
        };
      }
      group.add(sprite);
    }

    this.scene.add(group);

    const recordKey =
      descriptor.kind === "prop"
        ? `prop:${descriptor.meta.prop_id}`
        : descriptor.kind === "container"
          ? `container:${descriptor.meta.container_id}`
          : `interact:${descriptor.meta.interact_id}`;
    const record: PropRecord =
      descriptor.kind === "container"
        ? { group, materials, textures, container: { slug, state: "closed" } }
        : descriptor.kind === "interact"
          ? { group, materials, textures, interact: { slug, state: "idle" } }
          : { group, materials, textures };
    this.records.set(recordKey, record);

    // Probe for the authored PNG and swap when it arrives.
    void this.loadAndSwap(recordKey, klass, slug, state);
  }

  /**
   * I15c — swap a container's art to the `open-full` (just opened) or
   * `open-empty` (looted) state. Regenerates the procedural fallback
   * with a state-appropriate label first, then kicks off the authored-PNG
   * fetch exactly as the initial `closed` path does — same caching,
   * same "missing PNG stays procedural" fallback. No-op for non-container
   * records and for repeat events on the same state (P16 makes the
   * events idempotent, but we guard here too so a future caller can't
   * thrash the texture pipeline).
   */
  private swapContainerState(containerId: string, nextState: ContainerState): void {
    const recordKey = `container:${containerId}`;
    const record = this.records.get(recordKey);
    if (!record?.container) return;
    if (record.container.state === nextState) return;

    record.container.state = nextState;
    const slug = record.container.slug;
    const proc = makeProceduralPropTexture(slug, labelForContainerState(nextState));
    this.replaceTexture(record, proc);
    void this.loadAndSwap(recordKey, "containers", slug, nextState);
  }

  /**
   * W21b — swap an interact object's art to its `activated` state. Same
   * pattern as `swapContainerState`: refresh the procedural fallback with
   * a state-appropriate glyph, then probe for the authored PNG. No-op
   * for non-interact records and for repeat activation.
   */
  private swapInteractState(interactId: string, nextState: InteractState): void {
    const recordKey = `interact:${interactId}`;
    const record = this.records.get(recordKey);
    if (!record?.interact) return;
    if (record.interact.state === nextState) return;

    record.interact.state = nextState;
    const slug = record.interact.slug;
    const proc = makeProceduralPropTexture(slug, nextState === "activated" ? "!" : "?");
    this.replaceTexture(record, proc);
    void this.loadAndSwap(recordKey, "interacts", slug, nextState);
  }

  /**
   * Fetch the authored PNG for a given (klass/slug/state) and, if present,
   * swap the record's materials onto it. A cache miss keeps the current
   * (procedural or previous-state) texture — no visual regression on
   * state transitions when art is only partially authored.
   */
  private async loadAndSwap(
    recordKey: string,
    klass: string,
    slug: string,
    state: string,
  ): Promise<void> {
    const url = worldObjectUrl(klass, slug, state);
    const cacheKey = `${klass}/${slug}/${state}`;
    const img = await loadAuthoredImage(cacheKey, url);
    if (!img) return;
    const record = this.records.get(recordKey);
    if (!record) return;
    // Guard against an out-of-order swap: if the container or interact
    // advanced to another state while the PNG was in flight, drop this result.
    if (record.container && record.container.state !== state) return;
    if (record.interact && record.interact.state !== state) return;
    this.replaceTexture(record, authoredImageToTexture(img));
  }

  private replaceTexture(record: PropRecord, texture: THREE.Texture): void {
    record.textures.forEach((t) => t.dispose());
    (record.textures as THREE.Texture[]).length = 0;
    (record.textures as THREE.Texture[]).push(texture);
    for (const mat of record.materials) {
      if (
        (mat as THREE.SpriteMaterial).isSpriteMaterial ||
        (mat as THREE.MeshStandardMaterial).isMeshStandardMaterial
      ) {
        (mat as THREE.SpriteMaterial | THREE.MeshStandardMaterial).map = texture;
        mat.needsUpdate = true;
      }
    }
  }

  /** Test hook: which prop keys are currently in the scene. */
  getActiveKeys(): readonly string[] {
    return [...this.records.keys()];
  }

  dispose(): void {
    for (const record of this.records.values()) {
      this.scene.remove(record.group);
      for (const mat of record.materials) mat.dispose();
      for (const tex of record.textures) tex.dispose();
      record.group.traverse((obj) => {
        if ((obj as THREE.Mesh).geometry) {
          (obj as THREE.Mesh).geometry.dispose();
        }
      });
    }
    this.records.clear();
    for (const un of this.unsubscribes) un();
    this.unsubscribes.length = 0;
  }
}
